import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Pencil, Save, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateFacility } from '../../../../../../services/staffingService';

const FACILITY_TYPES = [
  'Office Building', 'Warehouse', 'Retail', 'Hospital', 'School',
  'Construction Site', 'Residential', 'Event Venue', 'Other',
];

function AddressAutocomplete({ value, onPlaceSelected, onClear }) {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => { setInputValue(value || ''); }, [value]);

  useEffect(() => {
    if (!window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'address_components', 'geometry'],
      componentRestrictions: { country: 'us' },
    });
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const comps = place.address_components || [];
      const data = {
        formatted_address: place.formatted_address,
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        city: '', state: '', zipcode: '', country: '',
      };
      comps.forEach(c => {
        if (c.types.includes('locality')) data.city = c.long_name;
        if (c.types.includes('administrative_area_level_1')) data.state = c.short_name;
        if (c.types.includes('postal_code')) data.zipcode = c.long_name;
        if (c.types.includes('country')) data.country = c.long_name;
      });
      setInputValue(place.formatted_address);
      onPlaceSelected(data);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder="Search for address…"
        className="w-full rounded-lg border border-border pl-8 pr-8 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background"
      />
      {inputValue && (
        <button type="button" onClick={() => { setInputValue(''); onClear(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm text-foreground">{value || <span className="italic text-muted-foreground/50">—</span>}</p>
    </div>
  );
}

/**
 * DetailsTab — facility info + site contact editor.
 * Props: facility, customerCompanyId, onFacilityUpdate(updated)
 */
const DetailsTab = ({ facility, customerCompanyId, onFacilityUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);

  const toForm = (f) => ({
    name:         f.name         || '',
    address:      f.address      || '',
    city:         f.city         || '',
    state:        f.state        || '',
    zipcode:      f.zipcode      || '',
    country:      f.country      || '',
    geoLocation:  f.geoLocation  || null,
    facilityType: f.facilityType || '',
    notes:        f.notes        || '',
    status:       f.status       || 'active',
    siteContact: {
      name:  f.siteContact?.name  || '',
      phone: f.siteContact?.phone || '',
      email: f.siteContact?.email || '',
    },
  });

  const [form, setForm] = useState(() => toForm(facility));

  // Keep form in sync when parent re-fetches
  useEffect(() => { setForm(toForm(facility)); }, [facility]);

  const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSC = (k, v) => setForm(p => ({ ...p, siteContact: { ...p.siteContact, [k]: v } }));

  const handleAddressSelected = (data) => {
    setForm(p => ({
      ...p,
      address:     data.address,
      city:        data.city,
      state:       data.state,
      zipcode:     data.zipcode,
      country:     data.country,
      geoLocation: { lat: data.latitude, lng: data.longitude },
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim())    return toast.error('Facility name is required');
    if (!form.address.trim()) return toast.error('Address is required');
    setSaving(true);
    try {
      const resp = await updateFacility(customerCompanyId, facility._id, form);
      onFacilityUpdate(resp.data);
      setEditing(false);
      toast.success('Facility updated');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { setForm(toForm(facility)); setEditing(false); };

  return (
    <div className="space-y-5 p-6">
      {/* Facility info */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Facility Information</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-xs text-[#404880] hover:underline">
              <Pencil size={12} /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleCancel}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <X size={12} /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 rounded-lg bg-[#404880] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </button>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
            <ReadField label="Name"          value={form.name} />
            <ReadField label="Address"       value={form.address} />
            <ReadField label="City / State"  value={[form.city, form.state].filter(Boolean).join(', ')} />
            <ReadField label="ZIP"           value={form.zipcode} />
            <ReadField label="Facility Type" value={form.facilityType} />
            <ReadField label="Status"        value={form.status} />
            {form.notes && (
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-0.5">Notes</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{form.notes}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Name <span className="text-red-500">*</span></label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-muted-foreground mb-1">
                Address <span className="text-red-500">*</span>
                <span className="ml-1 text-muted-foreground/60">(select from suggestions)</span>
              </label>
              <AddressAutocomplete
                value={form.address}
                onPlaceSelected={handleAddressSelected}
                onClear={() => set('address', '')}
              />
              {form.geoLocation?.lat && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-600">
                  <MapPin size={10} /> Coordinates captured
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Facility Type</label>
              <select value={form.facilityType} onChange={e => set('facilityType', e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background">
                <option value="">Select type…</option>
                {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-muted-foreground mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                rows={3} className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background resize-none" />
            </div>
          </div>
        )}
      </div>

      {/* Site Contact */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Site Contact</h3>
        {!editing ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm">
            <ReadField label="Name"  value={form.siteContact.name} />
            <ReadField label="Phone" value={form.siteContact.phone} />
            <ReadField label="Email" value={form.siteContact.email} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { label: 'Name',  key: 'name',  type: 'text' },
              { label: 'Phone', key: 'phone', type: 'tel' },
              { label: 'Email', key: 'email', type: 'email' },
            ].map(({ label, key, type }) => (
              <div key={key}>
                <label className="block text-xs text-muted-foreground mb-1">{label}</label>
                <input
                  type={type}
                  value={form.siteContact[key]}
                  onChange={e => setSC(key, e.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsTab;
